/**
 * Editable Availability + Team-Note cells for the box-truck My Drivers table. Both persist immediately to the
 * driver's boxTruckInfo (operator-gated, team-private) and lift the saved value back so the row updates in place.
 * Availability = Available / Unavailable / Booked + an optional context comment; the note = a single editable,
 * last-write-wins team note (mirrors the per-load team annotation). The read-only equivalents on the load-detail
 * Nearby Drivers list render the same badge via boxTruckAvailabilityBadge.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, StickyNote } from 'lucide-react'
import { BoxTruckDriverAvailability, BoxTruckDriverAvailabilityStatus, BoxTruckDriverTeamNote } from '@numeo/types-core'
import { Button, Modal } from '@/components/ds2'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { SnackbarType } from '@/utils/snackbar'
import { BOX_TRUCK_AVAILABILITY_OPTIONS, boxTruckAvailabilityBadge } from '../utils/format'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'
import { setBoxTruckDriverAvailability, setBoxTruckDriverNote } from '../services/boxTruckLoadsApi'

/** Mirrors the server cap (MAX_ANNOTATION_COMMENT). */
const MAX_NOTE = 2000

export const DriverAvailabilityCell = ({
    userId,
    availability,
    onSaved,
}: {
    userId: string
    availability: BoxTruckDriverAvailability | undefined | null
    onSaved: (a: BoxTruckDriverAvailability) => void
}) => {
    const { showSnackbar } = useSnackbar()
    // Portaled + anchored (same hook as DriverLocationEditor): an absolute panel would be clipped by the
    // tables' overflow-x-auto wrappers for rows near the bottom edge.
    const { open, anchorRect, containerRef, triggerRef, panelRef, close, togglePanel, panelPositionStyle } = useAnchoredPopover()
    const [saving, setSaving] = useState(false)
    // Unset availability READS as Available — a working driver is available until an operator says otherwise
    // (nothing is written until they actually pick a status).
    const current = availability?.status ?? 'available'
    const badge = boxTruckAvailabilityBadge(current)

    // Picking an option saves IMMEDIATELY (no modal, no Save button) — this is a plain dropdown.
    const pick = async (status: BoxTruckDriverAvailabilityStatus) => {
        close()
        if (status === current && availability) return
        setSaving(true)
        try {
            const saved = await setBoxTruckDriverAvailability(userId, status)
            onSaved(saved)
        } catch (e) {
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Couldn’t update availability')
        } finally {
            setSaving(false)
        }
    }

    const pillTint = (type: 'success' | 'warning' | 'danger' | undefined): string =>
        type === 'success'
            ? 'bg-success-soft text-success'
            : type === 'warning'
              ? 'bg-warning-soft text-warning'
              : type === 'danger'
                ? 'bg-danger-soft text-danger'
                : 'bg-accent-soft text-accent'

    return (
        <div ref={containerRef} className="inline-flex" onClick={(e) => e.stopPropagation()}>
            {/* ONE tinted pill (the availability colour fills the whole control) with the label + chevron —
                click opens a plain dropdown right underneath. */}
            <button
                ref={triggerRef}
                type="button"
                onClick={togglePanel}
                title="Set availability"
                disabled={saving}
                className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-opacity hover:opacity-80 disabled:opacity-60 ${pillTint(badge?.type)}`}
            >
                <span>{badge?.label ?? 'Set status'}</span>
                <ChevronDown size={13} className={`shrink-0 opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={panelRef}
                        style={{ ...panelPositionStyle, minWidth: 140, width: undefined }}
                        className="flex flex-col gap-1 rounded-xl border border-border bg-surface p-1.5 shadow-overlay"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {BOX_TRUCK_AVAILABILITY_OPTIONS.map((o) => (
                            <button
                                key={o.value}
                                type="button"
                                onClick={() => void pick(o.value)}
                                className={`flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-xs font-medium transition-colors hover:opacity-80 ${pillTint(o.type)} ${
                                    o.value === current ? 'ring-1 ring-current/40' : ''
                                }`}
                            >
                                {o.label}
                                {o.value === current && <Check size={12} className="shrink-0" />}
                            </button>
                        ))}
                    </div>,
                    document.body
                )}
        </div>
    )
}

export const DriverNoteCell = ({
    userId,
    note,
    onSaved,
}: {
    userId: string
    note: BoxTruckDriverTeamNote | undefined | null
    onSaved: (n: BoxTruckDriverTeamNote | null) => void
}) => {
    const { showSnackbar } = useSnackbar()
    const [open, setOpen] = useState(false)
    const [text, setText] = useState(note?.text ?? '')
    const [saving, setSaving] = useState(false)

    const openModal = () => {
        setText(note?.text ?? '')
        setOpen(true)
    }
    const save = async () => {
        setSaving(true)
        try {
            const saved = await setBoxTruckDriverNote(userId, text)
            onSaved(saved)
            setOpen(false)
        } catch (e) {
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Couldn’t save the note')
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            {note?.text ? (
                <button type="button" onClick={openModal} title={note.text} className="flex max-w-32 items-center gap-1 text-left text-danger">
                    <StickyNote size={13} className="shrink-0" strokeWidth={1.75} />
                    <span className="truncate text-xs">{note.text}</span>
                </button>
            ) : (
                <button
                    type="button"
                    onClick={openModal}
                    className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                    title="Add a team note"
                >
                    <StickyNote size={13} strokeWidth={1.75} /> Add note
                </button>
            )}
            {open && (
                <Modal
                    open
                    onClose={() => !saving && setOpen(false)}
                    size="sm"
                    header={<h2 className="text-base font-semibold text-foreground">Team note</h2>}
                    footer={
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={saving}>
                                Cancel
                            </Button>
                            <Button variant="primary" size="sm" loading={saving} onClick={() => void save()}>
                                Save note
                            </Button>
                        </div>
                    }
                >
                    <p className="mb-2 text-xs text-foreground-muted">A shared note for your team about this driver — everyone on your team sees it.</p>
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value.slice(0, MAX_NOTE))}
                        rows={5}
                        placeholder="Add a note for your team…"
                        className="w-full resize-none rounded-lg border border-border bg-surface px-2.5 py-2 text-sm text-foreground outline-none focus:border-accent"
                    />
                </Modal>
            )}
        </>
    )
}
